import React, { useState } from 'react';
import { SparklesIcon } from '../constants';
import { suggestTags } from '../services/geminiService';

interface TagManagerProps {
  tags: string[];
  content: string;
  onChange: (tags: string[]) => void;
}

const TagManager: React.FC<TagManagerProps> = ({ tags, content, onChange }) => {
  const [newTag, setNewTag] = useState('');
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);

  const addTag = (tag: string) => {
    const newTags = [...new Set([...(tags || []), tag.toLowerCase()])];
    onChange(newTags);
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && newTag.trim() !== '') {
      e.preventDefault();
      addTag(newTag.trim());
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    onChange(tags.filter(tag => tag !== tagToRemove));
  };

  const handleAddSuggestedTag = (tag: string) => {
    addTag(tag);
    setSuggestedTags(current => current.filter(t => t !== tag));
  };

  const handleSuggestTags = async () => {
    if (!content.trim()) return;
    setIsSuggesting(true);
    setSuggestedTags([]);
    const result = await suggestTags(content);
    const currentTagsLower = new Set((tags || []).map(t => t.toLowerCase()));
    const seen = new Set<string>();
    const newSuggestions = result.filter(tag => {
      const lower = tag.toLowerCase().trim();
      if (!lower || currentTagsLower.has(lower) || seen.has(lower)) {
        return false;
      }
      seen.add(lower);
      return true;
    });
    setSuggestedTags(newSuggestions);
    setIsSuggesting(false);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="label-xs mr-1">Tags</span>
        {(tags || []).map(tag => (
          <span key={tag} className="inline-flex items-center h-6 pl-2 pr-0.5 rounded bg-ink/[0.06] font-mono text-[12px] text-ink/85">
            {tag}
            <button
              type="button"
              onClick={() => handleRemoveTag(tag)}
              className="ml-0.5 h-5 w-5 inline-flex items-center justify-center rounded text-graphite hover:text-ink hover:bg-ink/[0.08]"
              aria-label={`Tag ${tag} entfernen`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={newTag}
          onChange={(e) => setNewTag(e.target.value)}
          onKeyDown={(e) => {
            handleAddTag(e);
            if (e.key === 'Backspace' && newTag === '' && tags.length > 0) handleRemoveTag(tags[tags.length - 1]);
          }}
          onBlur={() => { if (newTag.trim()) { addTag(newTag.trim()); setNewTag(''); } }}
          placeholder="+ Tag"
          aria-label="Tag hinzufügen"
          className="h-6 w-28 bg-transparent border-0 shadow-none focus:ring-0 py-0 font-mono text-[12px] text-ink placeholder:text-graphite/70 px-1 rounded focus:bg-paper focus:outline-none"
        />
        <button
          onClick={handleSuggestTags}
          disabled={isSuggesting || !content.trim()}
          className="inline-flex items-center gap-1 h-6 px-2 rounded text-xs text-graphite hover:text-ink hover:bg-ink/[0.06] disabled:opacity-40 disabled:cursor-not-allowed"
          title="Tags per KI aus dem Inhalt vorschlagen"
        >
          <SparklesIcon className="h-3.5 w-3.5 text-kind-prompt" />
          {isSuggesting ? 'Sucht …' : 'Vorschlagen'}
        </button>
      </div>
      {suggestedTags.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-graphite mr-1">Vorschläge:</span>
          {suggestedTags.map(tag => (
            <button
              key={tag}
              onClick={() => handleAddSuggestedTag(tag)}
              className="inline-flex items-center h-6 px-2 rounded border border-dashed border-rule font-mono text-[12px] text-ink/80 hover:border-graphite hover:text-ink"
            >
              + {tag}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagManager;
